/**
 * @see https://docs.vouched.id/recipes/integrate-vouched-with-your-react-typescript-app.
 */
import dayjs from "dayjs";

const VOUCHED_API_KEY = process.env.NEXT_PUBLIC_VOUCHED_API_KEY ?? "";
const VOUCHED_ENV = process.env.NEXT_PUBLIC_VOUCHED_ENV;

export interface IVouchedConfig {
    verification?: IVouchedConfig.IVerification;
    onDone(job: IVouchedConfig.IJob): void;
}
export namespace IVouchedConfig {
    export interface IVerification {
        firstName?: string;
        lastName?: string;
        birthDate?: Date;
        email?: string;
        phone?: string;
    }

    /**
     * @see https://docs.vouched.id/reference/findjobs.
     */
    export type VerificationErrorType =
        | "InvalidRequestError"
        | "FaceMatchError"
        | "NameMatchError"
        | "BarcodeMatchError"
        | "BirthDateMatchError"
        | "ExpiredIdError"
        | "InvalidIdPhotoError"
        | "UnsupportedIdPhotoError"
        | "InvalidUserPhotoError"
        | "AuthenticationError"
        | "ConnectionError"
        | "UnknownSystemError"
        | "InvalidIdError"
        | "TooManyRequestsError";
    export interface IVerificationError {
        type: VerificationErrorType;
        message: string;
        warning: string;
    }

    /**
     * @see https://docs.vouched.id/reference/findjobs.
     */
    export interface IJob {
        id: string;
        token: string;
        status: string;
        result: {
            success: boolean;
        };
        errors: IVerificationError[];
    }
}

/**
 * Mounts VouchedJS onto the provided @param elementId container.
 * Can only be run after the script is fully loaded.
 *
 * @returns An unmount function.
 */
export function loadVouched(
    elementId: string,
    { verification: { firstName, lastName, birthDate, email, phone } = {}, onDone }: IVouchedConfig,
): () => void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vouched = (window as any).Vouched({
        ...BASE_VOUCHED_CONFIG,
        verification: {
            firstName,
            lastName,
            birthDate: birthDate == null ? undefined : dayjs(birthDate).format("MM/DD/YYY"),
            email,
            phone,
        },
        onDone,
    });

    const locator = `#${elementId}`;
    vouched.mount(locator);

    return () => vouched.unmount(locator);
}

const IS_SANDBOX = VOUCHED_ENV === "sandbox";
const BASE_VOUCHED_CONFIG = {
    appId: VOUCHED_API_KEY,
    sandbox: IS_SANDBOX,

    id: "camera",
    liveness: "orientation",
    includeBarcode: true,
    manualCaptureTimeout: IS_SANDBOX ? 3000 : undefined,

    // Mobile handoff:
    crossDevice: true,
    crossDeviceQRCode: true,
    crossDeviceSMS: true,

    theme: { name: "avant" },
};
